import React, { useState, forwardRef } from 'react';
import { Eye, EyeOff } from 'lucide-react';

export interface PasswordFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  containerClassName?: string;
  inputClassName?: string;
  labelClassName?: string;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(({
  label,
  error,
  helperText,
  containerClassName = '',
  inputClassName = '',
  labelClassName = '',
  id,
  name,
  disabled,
  style,
  ...restProps
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const inputId = id || name || 'password-field';

  return (
    <div className={`password-field-container flex flex-col gap-1 w-full ${containerClassName}`} style={{ position: 'relative', marginBottom: '1rem', ...style }}>
      {label && (
        <label
          htmlFor={inputId}
          className={`block text-xs font-semibold uppercase tracking-wider text-slate-400 ${labelClassName}`}
          style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#94a3b8', marginBottom: '0.25rem' }}
        >
          {label}
        </label>
      )}

      <div style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}>
        <input
          ref={ref}
          id={inputId}
          name={name}
          type={showPassword ? 'text' : 'password'}
          disabled={disabled}
          className={`w-full px-3 py-2 pr-10 bg-slate-900 border ${error ? 'border-red-500' : 'border-slate-700'} rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all ${inputClassName}`}
          style={{
            width: '100%',
            padding: '0.625rem 2.5rem 0.625rem 0.75rem',
            borderRadius: '0.375rem',
            border: error ? '1px solid #ef4444' : '1px solid #334155',
            backgroundColor: '#0f172a',
            color: '#f8fafc',
            fontSize: '0.95rem',
            outline: 'none',
            boxSizing: 'border-box'
          }}
          {...restProps}
        />

        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowPassword(prev => !prev);
          }}
          disabled={disabled}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          style={{
            position: 'absolute',
            right: '0.625rem',
            background: 'transparent',
            border: 'none',
            cursor: disabled ? 'not-allowed' : 'pointer',
            padding: '0.25rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#94a3b8',
            borderRadius: '4px'
          }}
          className="focus:outline-none focus:ring-2 focus:ring-amber-500 hover:text-amber-400"
        >
          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>

      {error ? (
        <p className="text-xs text-red-400 mt-1" style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>
          {error}
        </p>
      ) : helperText ? (
        <p className="text-xs text-slate-500 mt-1" style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '0.25rem' }}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
});

PasswordField.displayName = 'PasswordField';
